import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import useBaseSWR, {
  BareFetcher,
  Key,
  SWRConfiguration,
  SWRResponse,
} from 'swr';
import { v4 as uuidV4 } from 'uuid';

import { addRequestKey, removeRequestKey } from './slice';

export interface UseSWROutput<T> extends SWRResponse<T, Error> {
  loading: boolean;
  failedToLoad: boolean;
}

export const useSWR = <T>(
  key: Key,
  fn?: BareFetcher<T>,
  config?: SWRConfiguration<T, Error>,
): UseSWROutput<T> => {
  const keyId = useMemo(() => uuidV4(), []);
  const dispatch = useDispatch();
  const prevKey = useRef<Key | undefined>(undefined);

  const result = useBaseSWR<T>(key, fn || null, config);
  const { data, error } = result;

  const actualKey = typeof key === 'function' ? key() : key;

  useEffect(() => {
    if (prevKey.current === undefined || !isEqual(actualKey, prevKey.current)) {
      dispatch(addRequestKey(keyId, actualKey));
    }

    prevKey.current = actualKey;
  }, [dispatch, actualKey, keyId]);

  useEffect(() => {
    return function cleanup() {
      dispatch(removeRequestKey(keyId));
    };
  }, [dispatch, keyId]);

  return {
    ...result,
    loading: !data && !error,
    failedToLoad: !!error,
  };
};
